import { useCallback, useEffect, useState } from "react";
import { NumericInput } from "../../../shared";
import { DataViewerProps } from "../DataViewer";
import { getRadarFrame } from "../ProjectWithData";
import { FrameWithOpacity, RadarChart, RadarChartBounds } from "./RadarChart";

export const RadarViewer: React.FC<DataViewerProps> = ({ project, currentTimeS, onFrameChange }) => {
    const [renderData, setRenderData] = useState<FrameWithOpacity[]>([]);
    const [bounds, setBounds] = useState<RadarChartBounds>();
    const [prevFramesToRender, setPrevFramesToRender] = useState<number | undefined>(0);

    //Calculate bounds so that we can set them for our charts
    useEffect(() => {
        var newMinX: number | undefined;
        var newMaxX: number | undefined;
        var newMinY: number | undefined;
        var newMaxY: number | undefined;

        project.radarFrames?.forEach(frame => {
            frame.tlvs.forEach(tlv => {
                tlv.pointClouds.forEach(pc => {
                    newMinX = Math.min(pc.x ?? 0, newMinX ?? pc.x ?? 0);
                    newMinY = Math.min(pc.y ?? 0, newMinY ?? pc.y ?? 0);
                    newMaxX = Math.max(pc.x ?? 0, newMaxX ?? pc.x ?? 0);
                    newMaxY = Math.max(pc.y ?? 0, newMaxY ?? pc.y ?? 0);
                });
            });
        });

        setBounds({ minX: newMinX, maxX: newMaxX, minY: newMinY, maxY: newMaxY });
    }, [project.radarFrames]);

    //When time changes, get the current radar frame and update our chart data
    useEffect(() => {
        //Get frames we will be rendering for the current time
        const frame = getRadarFrame(project, currentTimeS);
        const framesToRender = new Array<FrameWithOpacity>();

        var opacity = 0;
        const opacityStep = 1 / ((prevFramesToRender ?? 0) + 1);
        for (var i = frame - (prevFramesToRender ?? 0); i <= frame; i++) {
            opacity += opacityStep;
            if (i < 0 || !project.radarFrames) continue;
            framesToRender.push({ opacity: opacity, number: i, frame: project.radarFrames[i] });
        }

        setRenderData(framesToRender);
    }, [project, currentTimeS, project.radarFrames, prevFramesToRender]);

    const lastFrame = renderData && renderData.length > 0 ? renderData[renderData.length - 1] : undefined;

    const handleChangeFrame = useCallback((delta: number) => {
        onFrameChange((currentTimeS) + (1 / ((project.radarFps ?? 10) + (delta < 0 ? .5 : -.5))) * delta)
    }, [currentTimeS, project.radarFps, onFrameChange]);

    if (!project.radarDataBinaryFile) {
        return <>No radar data was found.</>
    }
    else if (project.radarDataBinaryFile && !project.radarFrames?.length) {
        return <>Radar data was invalid or couldn't be parsed.<br />{project.radarParsingError}</>
    }

    return (
        !!project.radarFrames?.length && <>
            <RadarChart frames={renderData} bounds={bounds} targetColors={project.pointCloudColors} staticPointColor="#00FF00" />
            <div className="w-full flex flex-col gap-3 justify-content-center place-content-center place-items-center" >
                <div className="flex flex-row  justify-content-center place-content-center place-items-center">
                    <div>
                        <button onClick={() => handleChangeFrame(-5)}>-5</button>
                    </div>
                    <div>
                        <button onClick={() => handleChangeFrame(-1)}>-1</button>
                    </div>
                    <div className="w-32 text-center">
                        Frame: {lastFrame?.number}
                    </div>
                    <div>
                        <button onClick={() => handleChangeFrame(1)}>+1</button>
                    </div>
                    <div>
                        <button onClick={() => handleChangeFrame(5)}>+5</button>
                    </div>
                </div>
                <div><NumericInput label="Prev Frames to Render" allowFloat={false} className="inline" value={prevFramesToRender} onChange={setPrevFramesToRender} /></div>
            </div>
        </>
    );
}